import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    TextField,
    Typography,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { LinearProgress } from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WorkIcon from '@mui/icons-material/Work';
import { useGovernmentProjectsController } from "./context/government-projects-controller";
import { useGovernmentProjectsState } from "./context/government-projects-context";
import { IListGovernmentProjectsRequest } from "../../model/base/request/list-government-projects-request";
import PageHeader from "../components/shared/page-header";
import { useNavigate } from "../../hooks/useNavigate";

export function GovernmentProjectsComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadMoreData, setLoadMoreData] = useState(false);
    const { loadData } = useGovernmentProjectsController();
    const { governmentProjects } = useGovernmentProjectsState();
    const [paramsRequest, setParamsRequest] = useState<IListGovernmentProjectsRequest>({ page: 0, pageSize: 10 } as IListGovernmentProjectsRequest);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const searchData = async () => {
            setLoadMoreData(true);
            await loadData(paramsRequest);
            setLoadMoreData(false);
            setInitialLoading(false);
        };
        searchData();
    }, [paramsRequest]);

    const handleClearFilters = () => {
        setParamsRequest({ page: 0, pageSize: 10 } as IListGovernmentProjectsRequest);
    };

    const columns: GridColDef[] = [
        {
            field: 'projectName',
            headerName: 'Nome do Projeto',
            flex: 3,
        },
        {
            field: 'responsibleName',
            headerName: 'Responsável',
            flex: 2,
        },
        {
            field: 'projectTypeDescription',
            headerName: 'Tipo',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Chip
                    label={params.value || 'Não especificado'}
                    color="primary"
                    size="small"
                    variant="outlined"
                />
            ),
        },
        {
            field: 'link',
            headerName: 'Link',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                params.value ? (
                    <Link
                        href={params.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                        <OpenInNewIcon fontSize="small" />
                    </Link>
                ) : (
                    <span>-</span>
                )
            ),
        },
        {
            field: 'actions',
            headerName: 'Ações',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderCell: (params) => (
                <Button
                    variant="text"
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate(`/projetos-governamentais/${params.row.id}`)}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 500
                    }}
                >
                    Ver detalhes
                </Button>
            ),
        },
    ];

    return (
        <Box>
            <PageHeader
                title="Projetos Governamentais"
                subtitle="Conheça os projetos e iniciativas desenvolvidos pelo município para melhorar a qualidade de vida da população."
                breadcrumbs={[{ label: 'Projetos Governamentais' }]}
            />

            {initialLoading ? (
                <Skeleton variant="rounded" width="100%" height={500} sx={{ borderRadius: 2 }} />
            ) : (
                <React.Fragment>
                    <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={12} md={7}>
                                <TextField
                                    fullWidth
                                    label="Buscar"
                                    placeholder="Digite para buscar"
                                    value={paramsRequest.term || ''}
                                    onChange={(e) => {
                                        setParamsRequest({ ...paramsRequest, page: 0, term: e.target.value });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Tipo de Projeto</InputLabel>
                                    <Select
                                        value={paramsRequest.projectTypeId || ''}
                                        label="Tipo de Projeto"
                                        onChange={(e) =>
                                            setParamsRequest({
                                                ...paramsRequest,
                                                page: 0,
                                                projectTypeId: e.target.value as number,
                                            })
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>Todos</em>
                                        </MenuItem>
                                        <MenuItem value={1}>PROJETO</MenuItem>
                                        <MenuItem value={2}>PROGRAMAS</MenuItem>
                                        <MenuItem value={3}>ATIVIDADE</MenuItem>
                                        <MenuItem value={4}>AÇÕES</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    fullWidth
                                    sx={{ height: '56px' }}
                                >
                                    Limpar Filtros
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper sx={{ p: 3, minHeight: 500 }} elevation={2}>
                        {loadMoreData ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                                <LinearProgress sx={{ width: '50%' }} />
                            </Box>
                        ) : governmentProjects?.items?.length === 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                                <Typography variant="h6" color="text.secondary">
                                    Nenhum projeto governamental encontrado
                                </Typography>
                            </Box>
                        ) : isMobile ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {(governmentProjects?.items || []).map((project: any) => (
                                    <Paper key={project.id} elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                        <Box
                                            sx={{
                                                backgroundColor: 'primary.main',
                                                px: 2,
                                                py: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <WorkIcon sx={{ fontSize: 24, color: 'white' }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'white', flex: 1 }}>
                                                {project.projectName || '-'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ px: 2, py: 1.5 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                                                Responsável
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                {project.responsibleName || '-'}
                                            </Typography>
                                            <Chip
                                                label={project.projectTypeDescription || 'Não especificado'}
                                                color="primary"
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>
                                        <Divider />
                                        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                                            {project.link && (
                                                <Link
                                                    href={project.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 'auto' }}
                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </Link>
                                            )}
                                            <Button
                                                variant="text"
                                                size="small"
                                                endIcon={<ArrowForwardIcon />}
                                                onClick={() => navigate(`/projetos-governamentais/${project.id}`)}
                                                sx={{ textTransform: 'none', fontWeight: 500 }}
                                            >
                                                Ver detalhes
                                            </Button>
                                        </Box>
                                    </Paper>
                                ))}
                            </Box>
                        ) : (
                            <DataGrid
                                key={JSON.stringify(paramsRequest)}
                                rows={governmentProjects?.items || []}
                                rowCount={governmentProjects?.totalRecords || 0}
                                columns={columns}
                                getRowId={(row) => row.id}
                                hideFooterSelectedRowCount
                                paginationModel={{ page: paramsRequest.page, pageSize: paramsRequest.pageSize } as GridPaginationModel}
                                onPaginationModelChange={(e) => { setParamsRequest({ ...paramsRequest, page: e.page, pageSize: e.pageSize }) }}
                                pageSizeOptions={[5]}
                                paginationMode="server"
                                slots={{
                                    loadingOverlay: LinearProgress,
                                }}
                                loading={loadMoreData}
                                localeText={{
                                    MuiTablePagination: {
                                        labelDisplayedRows: ({ from, to, count }) =>
                                            `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`,
                                        labelRowsPerPage: 'Linhas por página:',
                                    },
                                    noRowsLabel: 'Nenhum registro encontrado',
                                    noResultsOverlayLabel: 'Nenhum resultado encontrado',
                                }}
                            />
                        )}
                    </Paper>
                </React.Fragment>
            )}
        </Box>
    );
}
